import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { cardProgressLabel } from "../../domain/practice/cards.ts";
import { useAppServices } from "../hooks/AppContext.tsx";
import { usePractice } from "../hooks/bindings.ts";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { WordType } from "../../domain/vocabulary/types.ts";
import { Frame, Screen, TopBar } from "./Shell.tsx";
import shell from "../design-system/ui.module.css";
import styles from "./flashcard.module.css";

const DRAG_THRESHOLD = 56;
const MAX_TILT = 14;
const FLY_X = 560;
const FLY_TILT = 22;

interface CardPose {
  dx: number;
  dy: number;
  rotate: number;
}

const REST_POSE: CardPose = { dx: 0, dy: 0, rotate: 0 };

export function PracticeFlashcard() {
  const { practice } = useAppServices();
  const state = usePractice();
  const navigate = useNavigate();
  const session = state.cardSession;
  const cardRef = useRef<HTMLDivElement>(null);
  const retryRef = useRef<HTMLButtonElement>(null);
  const drag = useRef<{ id: number; x: number; y: number; dx: number; dy: number } | null>(null);
  const flyTimer = useRef<number | null>(null);
  const flewOff = useRef(false);
  const [pose, setPose] = useState<CardPose>(REST_POSE);
  const [dragging, setDragging] = useState(false);
  const [flying, setFlying] = useState(false);
  const [showingBack, setShowingBack] = useState(false);
  const reduceMotion = usePrefersReducedMotion();
  const item = session?.items[session.cursor];

  useEffect(() => {
    if (session?.status === "completed" && !session.browsingHistory) {
      navigate("/practice/summary", { replace: true });
    }
  }, [navigate, session?.browsingHistory, session?.status]);

  useEffect(() => {
    return () => {
      if (flyTimer.current != null) {
        window.clearTimeout(flyTimer.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (!flewOff.current || session?.phase === "saving") {
      return;
    }
    flewOff.current = false;
    const card = cardRef.current;
    if (card) {
      card.style.transition = "none";
    }
    setDragging(false);
    setFlying(false);
    setPose(REST_POSE);
    const frame = window.requestAnimationFrame(() => {
      if (card) {
        card.style.transition = "";
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [item?.itemId, session?.phase]);

  useEffect(() => {
    setShowingBack(false);
  }, [item?.itemId]);

  useEffect(() => {
    if (!session || !item) {
      return;
    }
    if (session.phase === "save_failed") {
      retryRef.current?.focus();
      return;
    }
    cardRef.current?.focus();
  }, [item, session]);

  if (!session || !item) {
    return (
      <Frame>
        <Screen withNav={false}>
          <TopBar title="Cards" backTo="/practice" />
          <div className={shell.empty}>This card session is no longer available.</div>
        </Screen>
      </Frame>
    );
  }

  const activeSession = session;
  const progress = cardProgressLabel(activeSession);
  const graded = item.result != null;
  const busy = session.phase === "saving";
  const canReplace =
    session.status === "active" &&
    session.phase === "review" &&
    (item.result === "correct" || item.result === "incorrect");
  const canAnswer =
    canReplace ||
    (session.status === "active" && !graded && (session.phase === "hidden" || session.phase === "revealed"));
  const canGoForward = session.cursor < session.items.length - 1;
  const canReveal = session.phase === "hidden" && !graded && session.status === "active";
  const transform = reduceMotion
    ? undefined
    : `translate(${pose.dx}px, ${pose.dy}px) rotate(${pose.rotate}deg)`;

  function clearFlyTimer() {
    if (flyTimer.current != null) {
      window.clearTimeout(flyTimer.current);
      flyTimer.current = null;
    }
  }

  function resetDrag() {
    drag.current = null;
    setDragging(false);
    setFlying(false);
    setPose(REST_POSE);
  }

  function flyHorizontally(direction: 1 | -1) {
    if (reduceMotion) {
      resetDrag();
      void practice.grade(direction > 0 ? "correct" : "incorrect");
      return;
    }
    setDragging(false);
    setFlying(true);
    window.requestAnimationFrame(() => {
      setPose({ dx: direction * FLY_X, dy: 0, rotate: direction * FLY_TILT });
      const card = cardRef.current;
      let settled = false;
      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        clearFlyTimer();
        card?.removeEventListener("transitionend", onEnd);
        flewOff.current = true;
        setDragging(true);
        setFlying(false);
        void practice.grade(direction > 0 ? "correct" : "incorrect");
      };
      const onEnd = (event: TransitionEvent) => {
        if (event.propertyName !== "transform") {
          return;
        }
        finish();
      };
      card?.addEventListener("transitionend", onEnd);
      flyTimer.current = window.setTimeout(finish, 420);
    });
  }

  function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.button !== 0 || busy || flying) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.style.transition = "";
    clearFlyTimer();
    setFlying(false);
    setDragging(true);
    drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, dy: 0 };
  }

  function onPointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const current = drag.current;
    if (!current || current.id !== event.pointerId) {
      return;
    }
    current.dx = event.clientX - current.x;
    current.dy = event.clientY - current.y;
    const absX = Math.abs(current.dx);
    const absY = Math.abs(current.dy);
    if (!(absX > absY * 1.2 && canAnswer)) {
      setPose(REST_POSE);
      return;
    }
    const tilt = Math.max(-MAX_TILT, Math.min(MAX_TILT, current.dx / 12));
    setPose({ dx: current.dx, dy: 0, rotate: tilt });
  }

  function onPointerUp(event: React.PointerEvent<HTMLDivElement>) {
    const current = drag.current;
    drag.current = null;
    if (!current || current.id !== event.pointerId || busy) {
      resetDrag();
      return;
    }
    const absX = Math.abs(current.dx);
    const absY = Math.abs(current.dy);
    if (absX < DRAG_THRESHOLD && absY < DRAG_THRESHOLD) {
      resetDrag();
      setShowingBack((current) => !current);
      if (!showingBack && canReveal) {
        void practice.reveal();
      }
      return;
    }
    if (absX > absY * 1.2 && canAnswer) {
      flyHorizontally(current.dx > 0 ? 1 : -1);
      return;
    }
    resetDrag();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget || busy) {
      return;
    }
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      setShowingBack((current) => !current);
      if (!showingBack && canReveal) {
        void practice.reveal();
      }
      return;
    }
    if ((event.key === "ArrowRight" || event.key === "ArrowLeft") && canAnswer) {
      event.preventDefault();
      void practice.grade(event.key === "ArrowRight" ? "correct" : "incorrect");
    }
  }

  function goBack() {
    if (busy || activeSession.cursor === 0) {
      return;
    }
    void practice.previous();
  }

  function goForward() {
    if (busy) {
      return;
    }
    if (activeSession.browsingHistory && activeSession.cursor >= activeSession.items.length - 1) {
      practice.closeCardBrowse();
      navigate("/practice/summary");
      return;
    }
    if (canGoForward) {
      void practice.resume();
    }
  }

  return (
    <Frame>
      <Screen withNav={false}>
        <TopBar
          title="Cards"
          backTo="/practice"
          right={
            <button className={shell.textButton} type="button" onClick={() => practice.end(true)} disabled={busy}>
              End
            </button>
          }
        />
        <div className={styles.deck}>
        <p id="card-progress" className={styles.progress}>
          {progress.current} / {progress.total}
        </p>
        <div
          ref={cardRef}
          data-result={item.result === "correct" || item.result === "incorrect" ? item.result : undefined}
          className={`${styles.scene} ${styles.cardMotion} ${dragging ? styles.dragging : ""} ${flying ? styles.flying : ""} ${showingBack && reduceMotion ? styles.revealed : ""} ${pose.dx > 36 ? styles.know : ""} ${pose.dx < -36 ? styles.miss : ""}`}
          tabIndex={0}
          role="group"
          aria-labelledby="card-progress"
          onKeyDown={onKeyDown}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={resetDrag}
          style={{ transform }}
        >
          <div className={`${styles.flipper} ${showingBack && !reduceMotion ? styles.flipped : ""}`}>
            <div className={styles.front}>
              {session.direction === "hide_meaning" ? (
                <WordLine word={item.captured.wordDisplay} type={item.captured.type} />
              ) : (
                <p className={styles.meaning}>{item.captured.definition}</p>
              )}
            </div>
            <div className={styles.back} aria-hidden={!showingBack}>
              {session.direction === "hide_meaning" ? (
                <p className={styles.meaning}>{item.captured.definition}</p>
              ) : (
                <WordLine word={item.captured.wordDisplay} type={item.captured.type} />
              )}
              <p className={styles.example}>{item.captured.example}</p>
            </div>
          </div>
        </div>
        {session.phase === "save_failed" ? (
          <button ref={retryRef} className={`${shell.primary} ${styles.wide}`} type="button" onClick={() => void practice.retryCardSave()}>
            Retry save
          </button>
        ) : null}
        <div className={styles.dock}>
          <button className={styles.round} type="button" aria-label="Back" onClick={goBack} disabled={session.cursor === 0 || busy}>
            <ChevronLeft size={28} strokeWidth={1.75} />
          </button>
          <button
            className={styles.round}
            type="button"
            aria-label="Forward"
            onClick={goForward}
            disabled={busy || (!canGoForward && !(session.browsingHistory && session.cursor >= session.items.length - 1))}
          >
            <ChevronRight size={28} strokeWidth={1.75} />
          </button>
        </div>
        </div>
      </Screen>
    </Frame>
  );
}

const PART_OF_SPEECH: Record<WordType, string> = {
  noun: "n.",
  verb: "v.",
  phrasal_verb: "phr. v.",
  adjective: "adj.",
  adverb: "adv.",
  phrase: "phr.",
  idiom: "id.",
  other: "other",
};

function WordLine({ word, type }: { word: string; type: WordType }) {
  return (
    <h1 className={styles.word}>
      {word} <span className={styles.pos}>({PART_OF_SPEECH[type]})</span>
    </h1>
  );
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return reduced;
}
